import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useGame } from '../../contexts/GameContext';
import { gameSocket } from '../../lib/socket';
import LocationMarker from './LocationMarker';
import { Location, LocationConnection } from '../../types';
import './GameMap.css';

const MAP_URL = 'https://hyklrtbvjonovqstskza.supabase.co/storage/v1/object/public/map/map.png';
const MIN_SCALE = 0.5;
const MAX_SCALE = 2;
const SCALE_STEP = 0.1;

interface MapSettings {
  scale: number;
  position: { x: number; y: number };
}

const GameMap: React.FC = () => {
  const { state, loadLocations } = useGame();
  const { character, locations } = state;
  
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  
  // Ключ для localStorage
  const MAP_SETTINGS_KEY = character ? `map_settings_${character.id}` : 'map_settings_default';
  
  // Инициализация состояния из localStorage
  const [mapSettings, setMapSettings] = useState<MapSettings>(() => {
    try {
      const saved = localStorage.getItem(MAP_SETTINGS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }
    } catch (e) {
      console.error('Error loading map settings:', e);
    }
    return {
      scale: 1,
      position: { x: 0, y: 0 }
    };
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Состояние для перемещения
  const [availableConnections, setAvailableConnections] = useState<LocationConnection[]>([]);
  
  // Функция для ограничения позиции карты
  const constrainPosition = useCallback((x: number, y: number) => {
    const container = mapContainerRef.current;
    const map = mapRef.current;
    if (!container || !map) return { x, y };
    
    const containerRect = container.getBoundingClientRect();
    const mapWidth = 2048 * mapSettings.scale;
    const mapHeight = 1536 * mapSettings.scale;
    
    // Разрешаем выход за границы на 70% (30% должно оставаться внутри)
    const allowedOverflow = 0.7;
    
    // minX: карта может уйти влево на 70% своей ширины
    const minX = -(mapWidth * allowedOverflow);
    // maxX: карта может уйти вправо, оставив 30% слева
    const maxX = containerRect.width - mapWidth * (1 - allowedOverflow);
    
    // minY: карта может уйти вверх на 70% своей высоты
    const minY = -(mapHeight * allowedOverflow);
    // maxY: карта может уйти вниз, оставив 30% сверху
    const maxY = containerRect.height - mapHeight * (1 - allowedOverflow);
    
    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y))
    };
  }, [mapSettings.scale]);
  
  // Загружаем локации при монтировании
  useEffect(() => {
    console.log('GameMap: Loading locations...');
    loadLocations();
  }, [loadLocations]);
  
  // Загружаем доступные связи при изменении текущей локации
  useEffect(() => {
    if (character?.current_location_id?.type === 'in_location') {
      const currentLocationId = character.current_location_id.location_id;
      console.log('GameMap: Requesting connections for location:', currentLocationId);
      
      // Подписываемся на событие получения связей
      const handleConnections = (data: { connections: LocationConnection[] }) => {
        console.log('GameMap: Received connections:', data.connections);
        setAvailableConnections(data.connections);
      };
      
      gameSocket.on('location.connections', handleConnections);
      
      // Запрашиваем связи для текущей локации
      gameSocket.emit('location.get-connections', { locationId: currentLocationId });
      
      return () => {
        gameSocket.off('location.connections', handleConnections);
      };
    } else {
      setAvailableConnections([]);
    }
  }, [character?.current_location_id]);
  
  // Проверяем, можно ли переместиться в локацию
  const canTravelTo = useCallback((locationId: string): boolean => {
    if (character?.current_location_id?.type !== 'in_location') return false;
    
    const currentLocationId = character.current_location_id.location_id;
    
    return availableConnections.some(conn => 
      (conn.from_location_id === currentLocationId && conn.to_location_id === locationId) ||
      (conn.is_bidirectional && conn.from_location_id === locationId && conn.to_location_id === currentLocationId)
    );
  }, [character?.current_location_id, availableConnections]);
  
  // Получаем информацию о связи между локациями
  const getConnectionInfo = useCallback((toLocationId: string): LocationConnection | null => {
    if (character?.current_location_id?.type !== 'in_location') return null;
    
    const currentLocationId = character.current_location_id.location_id;
    
    return availableConnections.find(conn => 
      (conn.from_location_id === currentLocationId && conn.to_location_id === toLocationId) ||
      (conn.is_bidirectional && conn.from_location_id === toLocationId && conn.to_location_id === currentLocationId)
    ) || null;
  }, [character?.current_location_id, availableConnections]);
  
  // Начать путешествие
  const handleStartTravel = useCallback((locationId: string) => {
    gameSocket.emit('location.start-travel', { toLocationId: locationId });
  }, []);
  
  // Получить название текущей локации
  const getCurrentLocationName = useCallback(() => {
    if (character?.current_location_id?.type === 'in_location') {
      const currentLocation = locations.find(loc => 
        loc.id === (character.current_location_id as any).location_id
      );
      return currentLocation?.name || 'Неизвестная локация';
    }
    return 'Неизвестная локация';
  }, [character, locations]);
  
  // Сохраняем настройки в localStorage при изменении
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(MAP_SETTINGS_KEY, JSON.stringify(mapSettings));
    }
  }, [mapSettings, MAP_SETTINGS_KEY, isInitialized]);
  
  // Устанавливаем флаг инициализации и применяем ограничения к начальной позиции
  useEffect(() => {
    setIsInitialized(true);
    
    // Применяем ограничения к сохраненной позиции после монтирования
    const container = mapContainerRef.current;
    if (container) {
      const constrained = constrainPosition(mapSettings.position.x, mapSettings.position.y);
      if (constrained.x !== mapSettings.position.x || constrained.y !== mapSettings.position.y) {
        setMapSettings(prev => ({
          ...prev,
          position: constrained
        }));
      }
    }
  }, [constrainPosition, mapSettings.position.x, mapSettings.position.y]);
  
  // Предотвращаем прокрутку страницы при зуме карты
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;
    
    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault();
    };
    
    // Добавляем обработчик с { passive: false } для возможности preventDefault
    container.addEventListener('wheel', handleWheelNative, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleWheelNative);
    };
  }, []);
  
  // Обработка колеса мыши для зума
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, mapSettings.scale + delta));
    
    if (newScale !== mapSettings.scale) {
      // Получаем позицию курсора относительно контейнера
      const rect = mapContainerRef.current?.getBoundingClientRect();
      if (!rect) return;
      
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Вычисляем новую позицию, чтобы зум был относительно курсора
      const scaleRatio = newScale / mapSettings.scale;
      const newX = mouseX - (mouseX - mapSettings.position.x) * scaleRatio;
      const newY = mouseY - (mouseY - mapSettings.position.y) * scaleRatio;
      
      // Применяем ограничения к новой позиции
      const constrainedPosition = constrainPosition(newX, newY);
      
      setMapSettings({
        scale: newScale,
        position: constrainedPosition
      });
    }
  }, [mapSettings, constrainPosition]);
  
  // Начало перетаскивания
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Только левая кнопка мыши
    
    // Проверяем, не кликнули ли по интерактивным элементам
    const target = e.target as HTMLElement;
    if (target.closest('.location-marker-wrapper') || 
        target.closest('.location-travel-menu')) {
      return;
    }
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - mapSettings.position.x,
      y: e.clientY - mapSettings.position.y
    });
  }, [mapSettings.position]);
  
  // Перетаскивание
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    const constrained = constrainPosition(newX, newY);
    
    setMapSettings(prev => ({
      ...prev,
      position: constrained
    }));
  }, [isDragging, dragStart, constrainPosition]);
  
  // Конец перетаскивания
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Глобальные обработчики для перетаскивания
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        const constrained = constrainPosition(newX, newY);
        
        setMapSettings(prev => ({
          ...prev,
          position: constrained
        }));
      };
      
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };
      
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, constrainPosition]);
  
  return (
    <div 
      className="game-map-container"
      ref={mapContainerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div 
        className="game-map"
        ref={mapRef}
        style={{
          transform: `translate(${mapSettings.position.x}px, ${mapSettings.position.y}px) scale(${mapSettings.scale})`,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
      >
        <img 
          src={MAP_URL} 
          alt="Карта мира" 
          className="game-map-image"
          draggable={false}
        />
        
        {/* Маркеры локаций */}
        <div className="game-map-locations">
          {locations.map(location => (
            <LocationMarker
              key={location.id}
              location={location}
              isCurrentLocation={
                character?.current_location_id?.type === 'in_location' &&
                character.current_location_id.location_id === location.id
              }
              canTravel={canTravelTo(location.id)}
              character={character}
              travelTime={getConnectionInfo(location.id)?.travel_time || 60}
              onStartTravel={handleStartTravel}
              currentLocationName={getCurrentLocationName()}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GameMap;
